/**
 * Pure helpers for the wallet top-up "pending → landed" watch.
 *
 * A Stripe-funded top-up credits the wallet ledger only when a webhook lands
 * server-side — `payment_intent.processing` for bank (ACH) debits,
 * `payment_intent.succeeded` for everything else (see
 * `server/src/managers/stripe-wallet.ts` — the balance is never moved in the
 * request the browser's Payment Element confirms). So after a successful
 * confirm the client can't assume a single refetch will catch the credit; it
 * has to poll the balance until the money shows up. These helpers own the
 * bounded backoff schedule and the "did it land yet?" decision so the React
 * hook (`use-top-up-watch`) stays a thin timer shell and the logic is
 * unit-testable.
 */

import type { BillingFamilyResponse, BillingLedgerEntry } from '@/api/billing-family'

/**
 * One sentence, used by both the ledger-row badge and the balance-card note:
 * the money is spendable, the transfer is not final yet.
 */
export const PENDING_CLEARANCE_HINT =
  'Funds are available now. Bank transfers take a few business days to clear — if the transfer bounces, the credit is removed.'

/**
 * A top-up credited ahead of its bank transfer clearing (ACH). Card top-ups
 * and every other ledger kind carry no settlement state.
 */
export function isPendingClearance(entry: Pick<BillingLedgerEntry, 'settlementState'>): boolean {
  return entry.settlementState === 'PENDING_CLEARANCE'
}

export interface BuildTopUpPollDelaysOptions {
  /** Gap before the first re-poll. */
  initialMs?: number
  /** Multiplier applied to each successive gap (backoff). */
  factor?: number
  /** Per-step ceiling so late polls don't drift minutes apart. */
  maxStepMs?: number
  /** Total wall-clock budget; polling stops once the gaps sum past this. */
  budgetMs?: number
}

export const DEFAULT_TOP_UP_POLL_OPTIONS: Required<BuildTopUpPollDelaysOptions> = {
  initialMs: 1_000,
  factor: 1.6,
  maxStepMs: 8_000,
  budgetMs: 60_000,
}

/**
 * Backoff schedule (ms between successive balance refetches). Quick early
 * polls catch the common sub-3s webhook, easing out to `maxStepMs` and
 * stopping once the cumulative wait reaches `budgetMs` (~60s) — bounded, never
 * an infinite spinner.
 */
export function buildTopUpPollDelays(options: BuildTopUpPollDelaysOptions = {}): number[] {
  const { initialMs, factor, maxStepMs, budgetMs } = { ...DEFAULT_TOP_UP_POLL_OPTIONS, ...options }
  const delays: number[] = []
  let step = initialMs
  let total = 0
  while (total < budgetMs) {
    const next = Math.min(Math.max(1, Math.round(step)), maxStepMs)
    delays.push(next)
    total += next
    step *= factor
  }
  return delays
}

/**
 * A top-up has landed once the account's balance reaches what it held before
 * the charge plus the top-up amount (`targetBalanceCents`). Uses `>=` (not
 * `===`) so a concurrent credit that pushes the balance even higher still
 * counts as landed.
 */
export function topUpHasLanded(observedBalanceCents: number, targetBalanceCents: number): boolean {
  return observedBalanceCents >= targetBalanceCents
}

/**
 * Pull an account's current balance out of the cached BillingFamily query
 * payload(s). Returns null when the account isn't present (the hook then keeps
 * polling). Accepts the several cache entries that different `first=N` variants
 * may have produced and returns the first match.
 */
export function readAccountBalanceFromBillingCache(
  caches: ReadonlyArray<BillingFamilyResponse | undefined>,
  accountId: string,
): number | null {
  for (const cache of caches) {
    const account = cache?.me?.ownedAccounts.find((a) => a.id === accountId)
    if (account) {
      return account.balanceCents
    }
  }
  return null
}
